// The pure parts of the Tickets tab: the query it asks the server, and how a row's
// figures and codes read. The rows themselves are the server's
// (internal/report/tickets.go); nothing here decides a result.
import { contextOrganisation } from '../lib/ownerOrg'
import type { StaffKey, Vars } from '../lib/staffText'
import type { TicketReportRow, TicketResult } from '../lib/types'

/** What the Tickets tab asks for. The fields after channel are a clicked
 *  figure's (internal/api/ticketreport.go drillFilters); '' or undefined is unset. */
export type TicketFilters = {
  branches: string[]
  from: string
  to: string
  service: string
  staff: string
  result: string
  channel: string
  arrival?: string
  sources?: string[]
  priority?: string
  language?: string
  weekday?: number
  issuedFrom?: string
  issuedTo?: string
  call?: string
  counted?: string
  desk?: string
  outcome?: string
  mark?: string
  /** Only work that ended: what makes a ticket served. */
  ended?: boolean
  /** Tickets of bookings that came: 'all', a booked service's id, or 'none'. */
  booked?: string
  wait?: { by: 'minutes' | 'target'; lo: number; hi: number; notCalled?: boolean }
  /** Part of a ticket number; a search, never saved with a view. */
  number?: string
}

export const RESULTS: readonly TicketResult[] = ['served', 'redirected', 'no_show', 'cancelled', 'left', 'not_reached']

export const RESULT_KEY: Record<TicketResult, StaffKey> = {
  served: 'tickets.result.served',
  redirected: 'tickets.result.redirected',
  no_show: 'tickets.result.no_show',
  cancelled: 'tickets.result.cancelled',
  left: 'tickets.result.left',
  not_reached: 'tickets.result.not_reached',
}

/** The 16 columns in VTB's order, then the access token behind the step. */
export const COLUMNS: readonly StaffKey[] = [
  'tickets.col.branch', 'tickets.col.employee', 'tickets.col.section', 'tickets.col.desk',
  'tickets.col.number', 'tickets.col.date', 'tickets.col.taken', 'tickets.col.start',
  'tickets.col.end', 'tickets.col.serviceTime', 'tickets.col.wait', 'tickets.col.result',
  'tickets.col.redirected', 'tickets.col.redirectedBy', 'tickets.col.redirectedTo', 'tickets.col.channel',
  'tickets.col.viaToken',
]

/** The cursor stack the Tickets tab is paging through: the question it was built
 *  for (the server query string) and the `after` marker of every page shown so
 *  far, the first always `''`. */
export type Paging = { query: string; cursors: string[] }

/** The paging state for query: what it was if it is still the same question, else
 *  page one. A browser-side filter, period or branch change is a different
 *  question from the server's point of view, so it is never answered with a
 *  marker a previous question's page ended on — even one the reader is
 *  revisiting, such as Last 7 days → Yesterday → Last 7 days. The caller must
 *  write this back as state (not just read it) so a later question does not find
 *  this one's cursor stack still sitting there unreset. */
export function pagingFor(paging: Paging, query: string): Paging {
  return paging.query === query ? paging : { query, cursors: [''] }
}

/** A report's branches as the server reads them: every branch the reader reaches, with no id list. */
export const ALL_BRANCHES = 'all'

/** The branch= values, plus the owner's organisation when they mean all branches. */
export function appendBranches(p: URLSearchParams, branches: readonly string[], organisation = contextOrganisation()): void {
  for (const b of branches) p.append('branch', b)
  if (organisation && branches.length === 1 && branches[0] === ALL_BRANCHES) p.set('organisation', organisation)
}

/** What a choice of branches is sent as: all of several listed ones as ALL_BRANCHES. */
export function branchScope(chosen: readonly string[], listed: readonly { id: string }[]): string[] {
  const all = listed.length > 1 && listed.every((b) => chosen.includes(b.id))
  return all ? [ALL_BRANCHES] : [...chosen]
}

/** The query for the four routes: every branch, then only the filters set. */
export function ticketQuery(f: TicketFilters, after = '', limit?: number): string {
  const p = new URLSearchParams()
  appendBranches(p, f.branches)
  p.set('from', f.from)
  p.set('to', f.to)
  if (f.service) p.set('service', f.service)
  if (f.staff) p.set('staff', f.staff)
  if (f.result) p.set('result', f.result)
  if (f.channel) p.set('channel', f.channel)
  if (f.arrival) p.set('arrival', f.arrival)
  for (const s of f.sources ?? []) p.append('source', s)
  if (f.priority) p.set('priority', f.priority)
  if (f.language) p.set('language', f.language)
  if (f.weekday) p.set('weekday', String(f.weekday))
  if (f.issuedFrom) p.set('issuedFrom', f.issuedFrom)
  if (f.issuedTo) p.set('issuedTo', f.issuedTo)
  if (f.call) p.set('call', f.call)
  if (f.counted) p.set('counted', f.counted)
  if (f.desk) p.set('desk', f.desk)
  if (f.outcome) p.set('outcome', f.outcome)
  if (f.mark) p.set('mark', f.mark)
  if (f.ended) p.set('ended', '1')
  if (f.booked) p.set('booked', f.booked)
  if (f.number?.trim()) p.set('number', f.number.trim())
  if (f.wait) {
    p.set('waitBy', f.wait.by)
    if (f.wait.notCalled) p.set('notCalled', '1')
    else {
      p.set('waitLo', String(f.wait.lo))
      if (f.wait.hi > 0) p.set('waitHi', String(f.wait.hi))
    }
  }
  if (after) p.set('after', after)
  if (limit) p.set('limit', String(limit))
  return p.toString()
}

const zoneClocks = new Map<string, Intl.DateTimeFormat>()

/** A moment as HH:MM:SS on the branch's clock (zone), never the browser's. */
export function timeOf(iso: string | undefined, zone: string): string {
  const d = iso ? new Date(iso) : undefined
  if (!d || Number.isNaN(d.getTime())) return ''
  let format = zoneClocks.get(zone)
  if (!format) {
    const options = { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' } as const
    try {
      format = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: zone })
    } catch {
      // A zone this browser does not know: UTC rather than nothing.
      format = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' })
    }
    zoneClocks.set(zone, format)
  }
  const parts = format.formatToParts(d)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00'
  return `${get('hour')}:${get('minute')}:${get('second')}`
}

/** The Redirected column: No, or where the step went. */
export function redirectedText(row: TicketReportRow, say: (key: StaffKey, vars?: Vars) => string): string {
  switch (row.redirect) {
    case 'service':
      return row.redirectWhere ?? ''
    case 'back':
      return say('tickets.redirect.back')
    case 'person':
      return say('tickets.redirect.person')
    case 'desk':
      return say('tickets.redirect.desk', { code: row.redirectWhere ?? '' })
    default:
      return say('tickets.redirect.no')
  }
}
